import React from "react";
import Menu from "./components/menu";
import MenuItem from "./components/menuItem";
import { IconClock, IconDvir, IconEmail, IconHomeRoof, IconRoute, IconSettings3 } from "@geotab/zenith";
import { useRouterContext } from "../../../../ui/framework/hooks/RouterContext";
import { useStoreSelector } from "../../../../ui/framework/hooks/useStoreSelector";
import { useDispatcherEventsSelector } from "../../../../ui/framework/hooks/useDispatcherEventsSelector";
import { DispatcherEvents } from "../../../../utils/dispatcher";
import { useSecurityServiceContext } from "../../../../ui/framework/hooks/SecurityServiceContext";
import { useTranslationContext } from "../../../../ui/framework/hooks/TranslationContext";
import { isNoVehicle } from "../../../../utils/device";
import MenuTranslations from "./helpers/translations";

const AppMenu: React.FC = () => {
    const { router } = useRouterContext();
    const { translate } = useTranslationContext();
    const security = useSecurityServiceContext();
    const activeSection = useStoreSelector(window.driveApp.store, state => state.mobile.pageState.activeSection);
    const eldJurisdiction = useStoreSelector(window.driveApp.store, state => state.data.ELDJurisdiction);
    const hasNewMessages = useStoreSelector(window.driveApp.store, state => state.mobile.newMessage);

    useDispatcherEventsSelector(DispatcherEvents.UserUpdate);
    useDispatcherEventsSelector(DispatcherEvents.UserUpdateLanguage);
    useDispatcherEventsSelector(DispatcherEvents.UserUpdateRuleset);
    useDispatcherEventsSelector(DispatcherEvents.VehicleSelected);
    useDispatcherEventsSelector(DispatcherEvents.UserSwitch);

    const navigate = (link: string) => {
        router.navigate(link, true);
        const titleText = document.getElementById("title-text");
        if (titleText) {
            titleText.focus();
        }
    };

    const isActive = (idSuffix: string) => {
        const normalizedActive = activeSection?.replace("/", "-");
        return normalizedActive === idSuffix;
    };

    const isEWD = eldJurisdiction === "EWD";
    const canAccessHos = security.securityService.can("useHos") || (security.securityService.can("attemptHos") && isNoVehicle());
    const canAccessDvir = security.securityService.can("viewDvir") && !isNoVehicle();
    const canAccessMessaging = security.securityService.can("useMessaging");
    const canAccessTrips = security.securityService.can("showTrips");

    return (
        <Menu>
            <MenuItem
                id="menu-home"
                link="main"
                title={translate(MenuTranslations.Dashboard)}
                icon={IconHomeRoof}
                onClick={navigate}
                isActive={isActive("modules-main")} />
            {canAccessHos && (
                <MenuItem
                    id="menu-hos"
                    link="hos/main"
                    title={translate(isEWD ? MenuTranslations.WorkDiary : MenuTranslations.HOS)}
                    icon={IconClock}
                    onClick={navigate}
                    isActive={isActive("hos")} />
            )}
            {canAccessDvir && (
                <MenuItem
                    id="menu-dvir"
                    link="dvir/main"
                    title={translate(MenuTranslations.AssetInspection)}
                    icon={IconDvir}
                    onClick={navigate}
                    isActive={isActive("dvir")} />
            )}
            {canAccessMessaging && (
                <MenuItem
                    id="menu-messaging"
                    link="messaging/main"
                    title={translate(MenuTranslations.Messages)}
                    icon={IconEmail}
                    onClick={navigate}
                    hasIndicator={hasNewMessages}
                    isActive={isActive("modules-messaging")} />
            )}
            {(!(canAccessMessaging && canAccessDvir && canAccessHos) && canAccessTrips) && (
                <MenuItem
                    id="menu-trips"
                    link="trips/main"
                    title={translate(MenuTranslations.Trips)}
                    icon={IconRoute}
                    onClick={navigate}
                    isActive={isActive("trips")} />
            )}
            <MenuItem
                id="menu-other"
                link="other/main"
                title={translate(MenuTranslations.Settings)}
                icon={IconSettings3}
                onClick={navigate}
                isActive={isActive("modules-settings")} />
        </Menu>
    );
};

export default AppMenu;